/**
 * V11.5 — Page d'aide en ligne (/aide).
 *
 * - Charge le guide utilisateur brut depuis /api/docs/user-guides/raw
 * - Navigation par profil via onglets (Visiteur / Athlète / Coach / Club / Fédé / COPF / Admin)
 * - Recherche interne au clavier (Cmd/Ctrl+K) qui filtre les sections et surligne les correspondances
 * - Bouton "Télécharger le guide (MD)" pointant sur /api/docs/user-guides
 * - Lien direct vers /connexion pour les visiteurs
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { adminMailto } from "@/lib/adminContact";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import {
  BookOpen, Download, Search, HelpCircle, Users, ShieldCheck, Building2,
  Flag, Crown, Wrench, Home, Command,
} from "lucide-react";
import { API } from "@/lib/api";

// Profils avec labels FR + icônes + titre attendu dans le Markdown
const PROFILES = [
  { key: "visiteur",    label: "Visiteur",       icon: Home,        heading: "1. Visiteur (non connecté)" },
  { key: "athlete",     label: "Athlète",        icon: Users,       heading: "2. Athlète" },
  { key: "coach",       label: "Coach",          icon: ShieldCheck, heading: "3. Coach / Encadrant" },
  { key: "club",        label: "Club",           icon: Building2,   heading: "4. Club" },
  { key: "federation",  label: "Fédération",     icon: Flag,        heading: "5. Fédération" },
  { key: "copf",        label: "COPF",           icon: Crown,       heading: "6. COPF" },
  { key: "admin",       label: "Admin",          icon: Wrench,      heading: "7. Admin AITO" },
];

/**
 * Découpe le Markdown en sections par titre ##.
 * Retourne { intro, sections: { [heading]: md }, outro }
 */
function splitByH2(markdown) {
  const lines = (markdown || "").split("\n");
  const sections = {};
  let currentKey = "__intro__";
  let currentBuf = [];
  const push = () => {
    if (currentBuf.length > 0) {
      sections[currentKey] = (sections[currentKey] || "") + currentBuf.join("\n") + "\n";
      currentBuf = [];
    }
  };
  for (const line of lines) {
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      push();
      currentKey = m[1].trim();
      currentBuf.push(line);
    } else {
      currentBuf.push(line);
    }
  }
  push();
  return sections;
}

/**
 * Pour un profil donné, extrait la section correspondante par match approximatif
 * du "heading" attendu ("2. Athlète" match "2. Athlète (personnel)" etc.)
 */
function findSectionForProfile(sections, heading) {
  const keys = Object.keys(sections);
  const target = heading.toLowerCase();
  // Match exact d'abord
  for (const k of keys) {
    if (k.toLowerCase() === target) return sections[k];
  }
  // Sinon match par préfixe numérique (ex "2. Athl")
  const num = heading.match(/^(\d+)\./);
  if (num) {
    for (const k of keys) {
      if (k.startsWith(num[1] + ".")) return sections[k];
    }
  }
  return null;
}

/**
 * Surligne les occurrences d'un terme dans le markdown en enveloppant
 * les matches avec <mark>. Retourne un markdown modifié.
 */
function highlightMd(md, term) {
  if (!term || !term.trim() || !md) return md;
  const safe = term.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Attention : évite d'affecter les liens/URLs/images en s'appliquant uniquement au texte visible.
  // Simple heuristique : on remplace hors des balises markdown de lien "[...](...)"
  const regex = new RegExp(`(${safe})`, "gi");
  return md.replace(regex, "==$1==");
}

export default function HelpPage() {
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState("visiteur");
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  // Charge le markdown
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API}/docs/user-guides/raw`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        if (!cancel) setMarkdown(text);
      } catch (e) {
        if (!cancel) setError(e?.message || "Impossible de charger le guide.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Raccourci Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const isCombo = (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
      if (isCombo) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
      if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setSearch("");
        searchRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const sections = useMemo(() => splitByH2(markdown), [markdown]);

  const currentMd = useMemo(() => {
    const p = PROFILES.find((x) => x.key === profile);
    if (!p) return "";
    const raw = findSectionForProfile(sections, p.heading) || "";
    return highlightMd(raw, search).replace(/==([^=]+)==/g, "**$1**"); // fallback GFM
  }, [sections, profile, search]);

  // Filtrage par recherche : si un terme est saisi, on met en avant les profils qui contiennent le terme
  const matchesByProfile = useMemo(() => {
    if (!search.trim()) return {};
    const s = search.trim().toLowerCase();
    const out = {};
    for (const p of PROFILES) {
      const sec = findSectionForProfile(sections, p.heading) || "";
      const count = (sec.toLowerCase().match(new RegExp(s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")) || []).length;
      if (count > 0) out[p.key] = count;
    }
    return out;
  }, [sections, search]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-8" data-testid="help-page">
      {/* Header */}
      <header className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
              <HelpCircle className="h-8 w-8 text-[hsl(var(--aito-lagoon))]" />
              Centre d'aide AITO.PF
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Retrouve ici <strong>tout ce que tu peux faire sur AITO.PF</strong> selon ton profil :
              visiteur, athlète, coach, club, fédération, COPF ou administrateur.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${API}/docs/user-guides`}
              target="_blank"
              rel="noreferrer"
              onClick={() => toast.success("Téléchargement du guide utilisateur…")}
              data-testid="help-download-md"
            >
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-1.5" />
                Télécharger le guide (.md)
              </Button>
            </a>
          </div>
        </div>

        {/* Barre de recherche */}
        <Card className="mt-6 p-3 flex items-center gap-2" data-testid="help-search-card">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans le guide… (Cmd/Ctrl + K)"
            className="border-0 focus-visible:ring-0 shadow-none"
            data-testid="help-search-input"
          />
          <Badge variant="outline" className="text-[10px] gap-1 hidden sm:inline-flex">
            <Command className="h-3 w-3" /> K
          </Badge>
          {search && (
            <Button variant="ghost" size="sm" onClick={() => setSearch("")} data-testid="help-search-clear">
              Effacer
            </Button>
          )}
        </Card>

        {search && Object.keys(matchesByProfile).length > 0 && (
          <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span>Résultats trouvés dans :</span>
            {PROFILES.filter((p) => matchesByProfile[p.key]).map((p) => (
              <Badge
                key={p.key}
                variant="secondary"
                className="cursor-pointer hover:bg-[hsl(var(--aito-lagoon))]/10"
                onClick={() => setProfile(p.key)}
                data-testid={`help-match-${p.key}`}
              >
                {p.label} · {matchesByProfile[p.key]}
              </Badge>
            ))}
          </div>
        )}
      </header>

      {/* Contenu */}
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : error ? (
        <Card className="p-6 border-destructive/40 bg-destructive/5" data-testid="help-error">
          <div className="text-destructive font-medium mb-1">Guide indisponible</div>
          <p className="text-sm text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground mt-2">
            En attendant, vous pouvez <Link to="/" className="text-[hsl(var(--aito-lagoon))] hover:underline">retourner à l'accueil</Link>.
          </p>
        </Card>
      ) : (
        <Tabs value={profile} onValueChange={setProfile} className="w-full">
          <TabsList className="flex-wrap h-auto gap-1 bg-muted/50 p-1" data-testid="help-tabs">
            {PROFILES.map((p) => {
              const Icon = p.icon;
              const hasMatch = matchesByProfile[p.key] > 0;
              return (
                <TabsTrigger
                  key={p.key}
                  value={p.key}
                  className="text-xs sm:text-sm data-[state=active]:bg-background gap-1.5"
                  data-testid={`help-tab-${p.key}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{p.label}</span>
                  {hasMatch && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{matchesByProfile[p.key]}</Badge>}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {PROFILES.map((p) => (
            <TabsContent key={p.key} value={p.key} className="mt-4" data-testid={`help-content-${p.key}`}>
              <Card className="p-5 sm:p-6">
                {currentMd && p.key === profile ? (
                  <MarkdownRenderer>{currentMd}</MarkdownRenderer>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    <BookOpen className="h-6 w-6 mb-2 text-muted-foreground/60" />
                    Contenu non disponible pour ce profil.
                  </div>
                )}

                {/* CTA contextuelle */}
                <div className="mt-6 pt-4 border-t flex items-center justify-between flex-wrap gap-3">
                  {p.key === "visiteur" ? (
                    <>
                      <p className="text-xs text-muted-foreground">Prêt à créer ta fiche ?</p>
                      <Link to="/connexion?next=/creer-ma-fiche">
                        <Button size="sm" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
                          Créer mon compte
                        </Button>
                      </Link>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground">Une question sur ton rôle ?</p>
                      <a href={adminMailto({ subject: "Question AITO.PF" })} className="text-xs text-[hsl(var(--aito-lagoon))] hover:underline">
                        Nous contacter →
                      </a>
                    </>
                  )}
                </div>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Footer d'aide */}
      <div className="mt-8 text-xs text-muted-foreground text-center">
        Guide mis à jour régulièrement.{" "}
        <a
          href={`${API}/docs/user-guides/raw`}
          target="_blank"
          rel="noreferrer"
          className="text-[hsl(var(--aito-lagoon))] hover:underline"
        >
          Voir la version brute (Markdown)
        </a>
      </div>
    </div>
  );
}
